import { splitMultipleStringList } from "@frontend/common/src/business/DatabaseTable/utils/splitMultipleStringList";

import { useClassification } from "@/contexts/ClassificationContext";
import { useRegistryAgency } from "@/contexts/RegistryAgencyContext";
import { useTopicType } from "@/contexts/TopicTypeContext";
import { useArticleInfoQuery } from "@api/articleInfo/useArticleInfo";
import { usePatentPrimaryCategoryQuery } from "@api/patent/patentPrimaryCategory/usePatentPrimaryCategory";
import { ARTICLE_SECTION_CODE } from "@FillInTableContainer/constants/fillInTable.constants";
import useArticleSection from "@FillInTableContainer/hooks/useArticleSection";
import { FILL_IN_TABLE_KEY } from "@FillInTableContainer/shared/constants/fillInTableKey";

import TopFixInfoList from "./components/TopFixInfoList/TopFixInfoList";
import { TopFixInfoData } from "./components/types/topFixInfo.types";
import {
  DEFAULT_TOP_FIX_INFO_VALUE,
  TOP_FIX_INFO_CTG,
  TOP_FIX_INFO_DEFAULT,
  TOP_FIX_INFO_PATENT,
  TOP_FIX_INFO_PUBLICATION,
  TOP_FIX_INFO_REGULATORY,
} from "./constants/topFixInfo.constants";
import * as S from "./TopFixInfo.style";
import { separateTextAndTranslateCode } from "../../utils/separateTextAndTranslatedCode/separateTextAndTranslatedCode";

type Props = {
  disabledClassificationManual?: boolean;
};

const TOOLTIP_STYLE_INFO = {
  [TOP_FIX_INFO_PUBLICATION.affiliation.title]: {
    minWidth: "40rem",
    width: "100%",
  },
  [TOP_FIX_INFO_PUBLICATION.journalName.title]: {
    minWidth: "40rem",
    width: "100%",
  },
};

const TopFixInfo = ({ disabledClassificationManual }: Props) => {
  const { data: articleInfoFromQuery } = useArticleInfoQuery();
  const articleInfo = articleInfoFromQuery;
  const { pressClassificationData } = useClassification();
  const { topicTypeData } = useTopicType();
  const { RegistryAgencyData } = useRegistryAgency();
  const { data: patentPrimaryCategoryData } = usePatentPrimaryCategoryQuery();

  const articleSection = useArticleSection();

  const halfAndFullSectionList: string[] = [
    FILL_IN_TABLE_KEY.ClinicalTrials,
    FILL_IN_TABLE_KEY.ClinicalTrialStatus,
    FILL_IN_TABLE_KEY.Regulatory,
    FILL_IN_TABLE_KEY.ExpeditedProgram,
    FILL_IN_TABLE_KEY.PublicationJournal,
    FILL_IN_TABLE_KEY.ConferenceSchedule,
  ];
  const isHalfAndFullSection = halfAndFullSectionList.includes(articleSection);

  const onlyFT1EditorInfoList: string[] = [
    FILL_IN_TABLE_KEY.ClinicalTrialStatus,
    FILL_IN_TABLE_KEY.CompanyWebsiteVariousPages,
    FILL_IN_TABLE_KEY.PublicationJournal,
    FILL_IN_TABLE_KEY.ConferenceSchedule,
  ];
  const isOnlyFT1EditorInfo = onlyFT1EditorInfoList.includes(articleSection);

  const isPublication = articleSection === "Publication";

  const isRegulatory =
    articleInfo?.classificationManual === "regulatoryLabel" ||
    ARTICLE_SECTION_CODE[articleInfo?.classificationManual || ""] ===
      "regulatoryLabel";
  const isPatent =
    articleInfo?.classificationManual === "patent" ||
    ARTICLE_SECTION_CODE[articleInfo?.classificationManual || ""] === "patent";
  const isCTG = articleSection === "CTG" && !isRegulatory;

  const pressTitle = articleInfo?.pressTitle;
  const { code } = separateTextAndTranslateCode(pressTitle);

  const {
    classificationManual,
    classificationAi,
    draftArticleId,
    pressDate,
    manualPressDate,
    companyName,
    mcEditorName,
    ftEditorName,
    ftEditor2Name,
    pbEditorName,
  } = articleInfo || {};

  const PRESS_INFO: TopFixInfoData[] = [
    {
      content: `#${String(draftArticleId)}${code ? ` ${code}` : ""}`,
      ...TOP_FIX_INFO_DEFAULT.draftArticleId,
    },
    {
      content: pressDate,
      subContent: manualPressDate,
      ...TOP_FIX_INFO_DEFAULT.pressDate,
    },
    {
      content: companyName,
      ...TOP_FIX_INFO_DEFAULT.companyName,
    },
  ];

  const CLASSIFICATION_INFO: TopFixInfoData[] = [
    {
      content:
        pressClassificationData?.[classificationAi] ||
        DEFAULT_TOP_FIX_INFO_VALUE,
      ...TOP_FIX_INFO_DEFAULT.classificationAiName,
    },
    {
      content: pressClassificationData?.[classificationManual] || "",
      ...TOP_FIX_INFO_DEFAULT.classificationManualName,
    },
  ];

  const DEFAULT_EDITOR_INFO: TopFixInfoData[] = [
    {
      content: mcEditorName || DEFAULT_TOP_FIX_INFO_VALUE,
      ...TOP_FIX_INFO_DEFAULT.mcEditorName,
    },
    {
      content: ftEditor2Name || DEFAULT_TOP_FIX_INFO_VALUE,
      ...TOP_FIX_INFO_DEFAULT.ftEditor2Name,
    },
    {
      content: pbEditorName || DEFAULT_TOP_FIX_INFO_VALUE,
      ...TOP_FIX_INFO_DEFAULT.pbEditorName,
    },
  ];

  const ONLY_FT1_EDITOR_INFO: TopFixInfoData[] = [
    {
      content: mcEditorName || DEFAULT_TOP_FIX_INFO_VALUE,
      ...TOP_FIX_INFO_DEFAULT.mcEditorName,
    },
    {
      content: ftEditorName || DEFAULT_TOP_FIX_INFO_VALUE,
      ...TOP_FIX_INFO_DEFAULT.ftEditorName,
    },
    {
      content: pbEditorName || DEFAULT_TOP_FIX_INFO_VALUE,
      ...TOP_FIX_INFO_DEFAULT.pbEditorName,
    },
  ];

  const HALF_AND_FULL_EDITOR_INFO: TopFixInfoData[] = [
    {
      content: mcEditorName || DEFAULT_TOP_FIX_INFO_VALUE,
      ...TOP_FIX_INFO_DEFAULT.mcEditorName,
    },
    {
      content: ftEditorName || DEFAULT_TOP_FIX_INFO_VALUE,
      ...TOP_FIX_INFO_DEFAULT.ftEditorName,
    },
    {
      content: ftEditor2Name || DEFAULT_TOP_FIX_INFO_VALUE,
      ...TOP_FIX_INFO_DEFAULT.ftEditor2Name,
    },
    {
      content: pbEditorName || DEFAULT_TOP_FIX_INFO_VALUE,
      ...TOP_FIX_INFO_DEFAULT.pbEditorName,
    },
  ];

  const PUBLICATION_INFO: TopFixInfoData[] = [
    {
      content: articleInfo?.publicationId
        ? String(articleInfo.publicationId)
        : DEFAULT_TOP_FIX_INFO_VALUE,
      ...TOP_FIX_INFO_PUBLICATION.publicationId,
    },
    {
      content: articleInfo?.doi || DEFAULT_TOP_FIX_INFO_VALUE,
      ...TOP_FIX_INFO_PUBLICATION.doi,
    },
    {
      content: articleInfo?.publicationDate || DEFAULT_TOP_FIX_INFO_VALUE,
      ...TOP_FIX_INFO_PUBLICATION.publicationDate,
    },
    {
      content: articleInfo?.journal
        ? `${articleInfo.journal}${
            articleInfo?.issn ? ` (${articleInfo.issn})` : ""
          }`
        : DEFAULT_TOP_FIX_INFO_VALUE,
      ...TOP_FIX_INFO_PUBLICATION.journalName,
    },
    {
      content: articleInfo?.affiliation || DEFAULT_TOP_FIX_INFO_VALUE,
      ...TOP_FIX_INFO_PUBLICATION.affiliation,
    },
    {
      content:
        topicTypeData?.[articleInfo?.topicType] || DEFAULT_TOP_FIX_INFO_VALUE,
      ...TOP_FIX_INFO_PUBLICATION.topicType,
    },
  ];

  const PUBLICATION_EDITOR_INFO: TopFixInfoData[] = [
    {
      content: mcEditorName || DEFAULT_TOP_FIX_INFO_VALUE,
      ...TOP_FIX_INFO_PUBLICATION.mcEditorName,
    },
    {
      content: ftEditor2Name || DEFAULT_TOP_FIX_INFO_VALUE,
      ...TOP_FIX_INFO_PUBLICATION.ftEditor2Name,
    },
  ];

  const CTG_INFO: TopFixInfoData[] = [
    {
      content:
        articleInfo?.id ||
        String(articleInfo?.draftArticleId) ||
        DEFAULT_TOP_FIX_INFO_VALUE,
      ...TOP_FIX_INFO_CTG.id,
    },
    {
      content: articleInfo?.registryId || DEFAULT_TOP_FIX_INFO_VALUE,
      ...TOP_FIX_INFO_CTG.registryId,
    },

    {
      content:
        RegistryAgencyData?.[articleInfo?.registryAgency || ""] ||
        articleInfo?.registryAgency ||
        DEFAULT_TOP_FIX_INFO_VALUE,
      ...TOP_FIX_INFO_CTG.registryAgency,
    },
    {
      content: articleInfo?.registryDate || DEFAULT_TOP_FIX_INFO_VALUE,
      ...TOP_FIX_INFO_CTG.registryDate,
    },
    {
      content:
        articleInfo?.organizationName?.join(", ") || DEFAULT_TOP_FIX_INFO_VALUE,
      ...TOP_FIX_INFO_CTG.organizationName,
    },
    {
      content:
        articleInfo?.sponsorName?.join(", ") || DEFAULT_TOP_FIX_INFO_VALUE,
      ...TOP_FIX_INFO_CTG.sponsorName,
    },
    {
      content:
        articleInfo?.collaboratorName?.join(", ") || DEFAULT_TOP_FIX_INFO_VALUE,
      ...TOP_FIX_INFO_CTG.collaboratorName,
    },
  ];

  const CTG_EDITOR_INFO: TopFixInfoData[] = [
    {
      content: mcEditorName || DEFAULT_TOP_FIX_INFO_VALUE,
      ...TOP_FIX_INFO_DEFAULT.mcEditorName,
    },
    {
      content: ftEditor2Name || DEFAULT_TOP_FIX_INFO_VALUE,
      ...TOP_FIX_INFO_DEFAULT.ftEditor2Name,
    },
  ];

  const regulatoryCompanyDisplay =
    articleInfo?.companyList?.join(", ") ||
    companyName ||
    DEFAULT_TOP_FIX_INFO_VALUE;

  const REGULATORY_INFO: TopFixInfoData[] = [
    {
      content: String(articleInfo?.id) || DEFAULT_TOP_FIX_INFO_VALUE,
      ...TOP_FIX_INFO_REGULATORY.id,
    },
    {
      content: articleInfo?.labelId
        ? splitMultipleStringList(articleInfo.labelId)
            .filter(Boolean)
            .join(", ")
        : DEFAULT_TOP_FIX_INFO_VALUE,
      ...TOP_FIX_INFO_REGULATORY.labelId,
    },
    {
      content: articleInfo?.regulatoryAgency || DEFAULT_TOP_FIX_INFO_VALUE,
      ...TOP_FIX_INFO_REGULATORY.regulatoryAgency,
    },
    {
      content:
        articleInfo?.labelDate ||
        articleInfo?.pressDate ||
        DEFAULT_TOP_FIX_INFO_VALUE,
      ...TOP_FIX_INFO_REGULATORY.labelDate,
    },
    {
      content: regulatoryCompanyDisplay,
      ...TOP_FIX_INFO_REGULATORY.companyName,
    },
    {
      content: articleInfo?.labelName
        ? splitMultipleStringList(articleInfo.labelName)
            .filter(Boolean)
            .join(", ")
        : DEFAULT_TOP_FIX_INFO_VALUE,
      ...TOP_FIX_INFO_REGULATORY.labelName,
    },
    {
      content: articleInfo?.classification || DEFAULT_TOP_FIX_INFO_VALUE,
      ...TOP_FIX_INFO_REGULATORY.classification,
    },
  ];

  const REGULATORY_EDITOR_INFO: TopFixInfoData[] = [
    {
      content: mcEditorName || DEFAULT_TOP_FIX_INFO_VALUE,
      ...TOP_FIX_INFO_DEFAULT.mcEditorName,
    },
    {
      content: ftEditor2Name || DEFAULT_TOP_FIX_INFO_VALUE,
      ...TOP_FIX_INFO_REGULATORY.ftEditorName,
    },
  ];

  const PATENT_INFO: TopFixInfoData[] = [
    {
      content: String(articleInfo?.id) || DEFAULT_TOP_FIX_INFO_VALUE,
      ...TOP_FIX_INFO_PATENT.id,
    },
    {
      content: articleInfo?.publicationNumber || DEFAULT_TOP_FIX_INFO_VALUE,
      ...TOP_FIX_INFO_PATENT.publicationNumber,
    },
    {
      content: articleInfo?.publicationDate || DEFAULT_TOP_FIX_INFO_VALUE,
      ...TOP_FIX_INFO_PATENT.publicationDate,
    },
    {
      content: articleInfo?.applicationDate || DEFAULT_TOP_FIX_INFO_VALUE,
      ...TOP_FIX_INFO_PATENT.applicationDate,
    },
    {
      content: articleInfo?.applicantName || DEFAULT_TOP_FIX_INFO_VALUE,
      ...TOP_FIX_INFO_PATENT.applicantName,
    },
    {
      // primaryCategory 코드(PAPC0x) → patent classification displayName 매핑
      content:
        patentPrimaryCategoryData?.[articleInfo?.primaryCategory || ""] ||
        articleInfo?.primaryCategory ||
        DEFAULT_TOP_FIX_INFO_VALUE,
      ...TOP_FIX_INFO_PATENT.primaryCategory,
    },
  ];

  const PATENT_EDITOR_INFO: TopFixInfoData[] = [
    {
      content: mcEditorName || DEFAULT_TOP_FIX_INFO_VALUE,
      ...TOP_FIX_INFO_DEFAULT.mcEditorName,
    },
    {
      content: ftEditor2Name || DEFAULT_TOP_FIX_INFO_VALUE,
      ...TOP_FIX_INFO_PATENT.ftEditorName,
    },
  ];

  const targetEditorInfo = () => {
    if (isOnlyFT1EditorInfo) {
      return ONLY_FT1_EDITOR_INFO;
    }
    if (isHalfAndFullSection) {
      return HALF_AND_FULL_EDITOR_INFO;
    }
    return DEFAULT_EDITOR_INFO;
  };

  if (isPublication) {
    return (
      <S.TopFixInfoWrapper>
        <S.ListWrapper $minWidth="56rem">
          <TopFixInfoList
            list={PUBLICATION_INFO}
            maxWidth="none"
            minWidth="none"
            tooltipStyleInfo={TOOLTIP_STYLE_INFO}
          />
        </S.ListWrapper>
        <S.ListWrapper $maxWidth="40rem">
          <TopFixInfoList
            list={PUBLICATION_EDITOR_INFO}
            maxWidth="none"
            minWidth="none"
            tooltipStyleInfo={TOOLTIP_STYLE_INFO}
          />
        </S.ListWrapper>
      </S.TopFixInfoWrapper>
    );
  }
  if (isCTG) {
    return (
      <S.TopFixInfoWrapper>
        <S.ListWrapper $minWidth="56rem">
          <TopFixInfoList list={CTG_INFO} maxWidth="none" minWidth="none" />
        </S.ListWrapper>
        <S.ListWrapper $maxWidth="40rem">
          <TopFixInfoList
            list={CTG_EDITOR_INFO}
            maxWidth="40rem"
            minWidth="none"
          />
        </S.ListWrapper>
      </S.TopFixInfoWrapper>
    );
  }

  if (isRegulatory) {
    return (
      <S.TopFixInfoWrapper>
        <S.ListWrapper $minWidth="56rem">
          <TopFixInfoList
            list={REGULATORY_INFO}
            maxWidth="none"
            minWidth="none"
          />
        </S.ListWrapper>
        <S.ListWrapper $maxWidth="40rem">
          <TopFixInfoList
            list={REGULATORY_EDITOR_INFO}
            maxWidth="40rem"
            minWidth="none"
          />
        </S.ListWrapper>
      </S.TopFixInfoWrapper>
    );
  }

  if (isPatent) {
    return (
      <S.TopFixInfoWrapper>
        <S.ListWrapper $minWidth="56rem">
          <TopFixInfoList list={PATENT_INFO} maxWidth="none" minWidth="none" />
        </S.ListWrapper>
        <S.ListWrapper $maxWidth="40rem">
          <TopFixInfoList
            list={PATENT_EDITOR_INFO}
            maxWidth="40rem"
            minWidth="none"
          />
        </S.ListWrapper>
      </S.TopFixInfoWrapper>
    );
  }

  const disableDuplicate =
    articleSection === "Deal Filing" || articleSection === "Funding Filing";

  return (
    <S.TopFixInfoWrapper>
      <S.ListWrapper $minWidth="24rem">
        <TopFixInfoList
          list={PRESS_INFO}
          maxWidth="none"
          minWidth="none"
          disableDuplicate={disableDuplicate}
        />
      </S.ListWrapper>
      <S.ListWrapper $maxWidth="48rem" $minWidth="24rem">
        <TopFixInfoList
          list={CLASSIFICATION_INFO}
          maxWidth="48rem"
          minWidth="24rem"
          disabledClassificationManual={disabledClassificationManual}
          disableDuplicate={disableDuplicate}
        />
      </S.ListWrapper>
      <S.ListWrapper $maxWidth={`${targetEditorInfo().length * 20}rem`}>
        <TopFixInfoList
          list={targetEditorInfo()}
          maxWidth="80rem"
          minWidth="none"
          tooltipStyleInfo={TOOLTIP_STYLE_INFO}
          disableDuplicate={disableDuplicate}
        />
      </S.ListWrapper>
    </S.TopFixInfoWrapper>
  );
};

export default TopFixInfo;
